import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ClipboardList, ShoppingCart, Wrench, ChevronRight } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

/**
 * Secondary "2nd column" sidebar for the Orders section.
 * Mirrors the visual pattern used by UltraAdminSettings (left aside with
 * grouped sub-sections), so the Orders area exposes its sub-pages —
 * Genel Siparişler & Servis Siparişleri — as flyout-style sibling links.
 *
 * Keep this component presentational only; routes & data live in their
 * respective pages.
 */
const OrdersSubSidebar = () => {
  const location = useLocation();
  const { language } = useLanguage();
  const tr = language === 'tr';

  const items = [
    {
      id: 'general-orders',
      label: tr ? 'Genel Siparişler' : 'General Orders',
      icon: ShoppingCart,
      path: '/ultra-admin/orders',
      // Active for the parent path AND any nested children (e.g. /pending)
      isActive: location.pathname === '/ultra-admin/orders'
        || location.pathname.startsWith('/ultra-admin/orders/'),
    },
    {
      id: 'service-orders',
      label: tr ? 'Servis Siparişleri' : 'Service Orders',
      icon: Wrench,
      path: '/ultra-admin/service-orders',
      isActive: location.pathname === '/ultra-admin/service-orders'
        || location.pathname.startsWith('/ultra-admin/service-orders/'),
    },
  ];

  return (
    <aside
      data-testid="orders-sub-sidebar"
      className="hidden md:block w-56 bg-white border-r border-gray-100 flex-shrink-0 py-4 sticky top-0 self-start h-screen overflow-y-auto"
    >
      <div className="flex items-center gap-2 px-4 mb-3">
        <ClipboardList className="h-4 w-4 text-purple-700" />
        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
          {tr ? 'Siparişler' : 'Orders'}
        </p>
      </div>
      <nav className="space-y-0.5 px-2">
        {items.map(({ id, label, icon: Icon, path, isActive }) => (
          <Link
            key={id}
            to={path}
            data-testid={`orders-sub-${id}`}
            className={`w-full flex items-center justify-between gap-3 px-3 py-2.5 rounded-xl text-sm transition-colors ${
              isActive
                ? 'bg-gray-900 text-white font-semibold'
                : 'text-gray-600 hover:bg-gray-50'
            }`}
          >
            <div className="flex items-center gap-2.5">
              <Icon className="h-4 w-4 flex-shrink-0" />
              <span>{label}</span>
            </div>
            {isActive && <ChevronRight className="h-3.5 w-3.5 opacity-60" />}
          </Link>
        ))}
      </nav>
    </aside>
  );
};

export default OrdersSubSidebar;
